import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { api, setAuthToken, type AuthUser } from "@/lib/api";

export type UserRole = AuthUser["role"];

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  isAuthenticated: boolean;
  hasRole: (...roles: UserRole[]) => boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem("ksm_token");
    if (!token) {
      setUser(null);
      return;
    }
    try {
      const me = await api.me();
      setUser(me);
      localStorage.setItem("ksm_user", JSON.stringify(me));
    } catch {
      setAuthToken(null);
      setUser(null);
      localStorage.removeItem("ksm_user");
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem("ksm_token");
      if (token) {
        await refreshUser();
      } else {
        const stored = localStorage.getItem("ksm_user");
        if (stored) {
          try { setUser(JSON.parse(stored)); } catch { localStorage.removeItem("ksm_user"); }
        }
      }
      setLoading(false);
    };
    init();
  }, [refreshUser]);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const { token, user: authUser } = await api.login(email, password);
      setAuthToken(token);
      setUser(authUser);
      localStorage.setItem("ksm_user", JSON.stringify(authUser));
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : "Error de autenticación" };
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setAuthToken(null);
    localStorage.removeItem("ksm_user");
  }, []);

  const hasRole = useCallback(
    (...roles: UserRole[]) => {
      if (!user) return false;
      return roles.includes(user.role);
    },
    [user]
  );

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAuthenticated: !!user, hasRole, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return ctx;
}
